import React, { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'

import { CountryCodes, ORG_USER_ROLES, strings } from '@constants'
import { createUser } from '@data'
import { addUser } from '@data/conn/post/organisation'
import { showLoader, showToast } from '@data/state/action'
import { toggleDrawer } from '@data/state/action/root'
import { Spacings } from '@styles'
import { DrawerBodyWrapper, DrawerFooterWrapper, TOAST_TYPE } from '@base'
import { Button, Input, Select } from '@baseV2'

import { InputsWrapper } from '../styles'

const ROLES = Object.entries(ORG_USER_ROLES).map(([key, value]) => ({ key, value }))
const MOBILE_REGEX = /^\d{7}\d+$/
const EMAIL_REGEX =
	/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

const PhoneRow = styled.div`
	display: flex;
	align-items: flex-end;
	gap: ${Spacings.SPACING_2B};

	& > *:first-child {
		width: 7rem;
		flex-shrink: 0;
	}
	& > *:last-child {
		flex: 1;
	}
`

const AddNewUser = ({ orgId, update, newAdmin }) => {
	const dispatch = useDispatch()
	const user = useSelector((_state) => _state.getIn(['user', 'user']))

	const [fullName, setFullName] = useState('')
	const [email, setEmail] = useState('')
	const [mobile, setMobile] = useState('')
	const [role, setRole] = useState(ROLES.find((r) => r.value === ORG_USER_ROLES.ORG_ADMIN) || ROLES[0])
	const [countryCode, setCountryCode] = useState(CountryCodes[0])
	const [touched, setTouched] = useState(false)

	const mobileValid = MOBILE_REGEX.test(mobile)
	const emailValid = !email || EMAIL_REGEX.test(email)
	const disabled = !fullName || !mobileValid || !emailValid

	const nameError = touched && !fullName ? strings('name_required') : ''
	const mobileError = touched && mobile && !mobileValid ? strings('enter_valid_num') : ''
	const emailError = touched && email && !emailValid ? strings('enter_valid_email') : ''

	const finish = (message, isError) => {
		dispatch(showToast(true, message, { type: isError ? TOAST_TYPE.ERROR : TOAST_TYPE.SUCCESS }))
		if (!isError) dispatch(toggleDrawer(false))
	}

	const submit = () => {
		setTouched(true)
		if (disabled) return
		const payload = {
			userId: user?.user_id,
			email,
			mobile: countryCode.dial_code.replace('+', '') + mobile,
			name: fullName,
		}
		dispatch(showLoader(true))
		const request = newAdmin
			? createUser({ ...payload, role: 'ADMIN' })
			: addUser({ ...payload, organisationId: orgId, role: role?.value })
		request
			.then(() => finish(`Successfully ${newAdmin ? 'created new admin user' : 'added user'}`))
			.catch((err) => {
				console.log(err)
				finish(newAdmin ? strings('user_already_exist') : 'Error while adding user', true)
			})
			.finally(() => {
				update?.()
				dispatch(showLoader(false))
			})
	}

	const roles = useMemo(() => ROLES, [])

	return (
		<>
			<DrawerBodyWrapper>
				<InputsWrapper>
					<Input
						label={strings('name')}
						id='user-name-v2'
						value={fullName}
						onChange={(e) => setFullName(e.target.value)}
						onBlur={() => setTouched(true)}
						placeholder={strings('your_full_name')}
						error={nameError}
					/>
					<PhoneRow>
						<Select
							id='user-country-code-v2'
							data={CountryCodes}
							displayKey='dial_code'
							primaryKey='name'
							value={countryCode}
							onChange={setCountryCode}
							noDropIcon
						/>
						<Input
							label={strings('mobile')}
							id='user-mobile-v2'
							type='tel'
							maxLength={20}
							value={mobile}
							onChange={(e) => setMobile((e.target.value ?? '').replace(/\D/g, ''))}
							onBlur={() => setTouched(true)}
							placeholder={strings('enter_your_mobile_num')}
							error={mobileError}
						/>
					</PhoneRow>
					<Input
						label={strings('email')}
						id='user-email-v2'
						type='email'
						value={email}
						onChange={(e) => setEmail(e.target.value)}
						onBlur={() => setTouched(true)}
						placeholder={strings('your_email_address')}
						error={emailError}
					/>
					{!newAdmin && (
						<Select
							id='user-role-v2'
							label={strings('role')}
							displayKey='value'
							primaryKey='key'
							placeholder={strings('select_role')}
							data={roles}
							value={role}
							onChange={setRole}
						/>
					)}
				</InputsWrapper>
			</DrawerBodyWrapper>
			<DrawerFooterWrapper>
				<Button color='primary' disabled={disabled} onClick={submit}>
					{`Create ${newAdmin ? 'Admin' : 'User'}`}
				</Button>
			</DrawerFooterWrapper>
		</>
	)
}

export default AddNewUser
